/**
 * F-0783 (`docs/concept-account-deletion-and-export.md` §4d, WAD-9): a work area deleted in one tab
 * is told to every other tab of this origin, so each re-reads its work areas at once rather than at
 * its next focus. A tab that was in the deleted work area then says so through the store's own
 * "isn't available any more" notice (I-1876) — the same path a deletion elsewhere takes.
 *
 * `BroadcastChannel` reaches the other tabs only, never the sender. Where it does not exist (an old
 * WebView), nothing is told and the next focus re-read finds the work area gone.
 */

const CHANNEL_NAME = 'featit-work-areas';

interface IWorkAreaDeletedMessage {
  type: 'work-area-deleted';
  workAreaId: string;
}

function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

/** Tell the other tabs that `workAreaId` was deleted. */
export function announceWorkAreaDeleted(workAreaId: string): void {
  const channel = openChannel();
  if (!channel) return;
  const message: IWorkAreaDeletedMessage = { type: 'work-area-deleted', workAreaId };
  channel.postMessage(message);
  channel.close();
}

/** Hear another tab's deletions; returns the way to stop listening. */
export function onWorkAreaDeletedElsewhere(listener: (workAreaId: string) => void): () => void {
  const channel = openChannel();
  if (!channel) return () => undefined;
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data as Partial<IWorkAreaDeletedMessage> | null;
    if (data?.type === 'work-area-deleted' && typeof data.workAreaId === 'string') {
      listener(data.workAreaId);
    }
  };
  return () => channel.close();
}
